import { ReactElement, forwardRef, ReactNode } from 'react';
import classNames from 'classnames/bind';
import Divider from '../Divider';
import Icon, { ICONS } from '../Icon';
import PrintForUser from './PrintForUser';
import styles from './PrintView.module.scss';

const cx = classNames.bind(styles);

interface PrintViewProps {
  children: ReactNode;
  isHeaderFooterVisible?: boolean;
  redistributionAllowed?: boolean;
  userFullName?: string;
  disclaimer?: string;
}

const renderPrintIcon = () => (
  <div className={cx('print-icon')}>
    <Icon name={ICONS.Brand} width={118} height={24} dataTestId="print-icon" />
  </div>
);

const DATA_TEST_ID = 'print-view';

const PrintView = (
  { children, isHeaderFooterVisible = false, redistributionAllowed = false, userFullName, disclaimer }: PrintViewProps,
  ref
): ReactElement => {
  const renderDisclaimer = () => {
    return (
      <div className={cx('disclaimer')}>
        <div className={cx('info')}>
          {renderPrintIcon()}
          <Divider />
          <span className={cx('contact')}>Questions? Contact via email at clientservices@creditsights.com</span>
          <span className={cx('copyright')}>
            Copyright &copy; {new Date().getFullYear().toString()} Fitch Solutions, Inc., Fitch Ratings, Inc. and their
            subsidiaries.
          </span>
        </div>
        {disclaimer && <div className={cx('content')} dangerouslySetInnerHTML={{ __html: disclaimer }} />}
      </div>
    );
  };

  const renderWithHeaderFooter = () => {
    return (
      <table ref={ref} className={cx('body')} data-testid={DATA_TEST_ID}>
        <thead className={cx('header')}>
          <tr>
            <th>
              <PrintForUser userFullName={userFullName} />
            </th>
          </tr>
        </thead>
        <tbody className={cx('content')}>
          <tr>
            <td>
              {renderPrintIcon()}
              {children}
              {renderDisclaimer()}
            </td>
          </tr>
        </tbody>
        {!redistributionAllowed && (
          <tfoot className={cx('footer')}>
            <tr>
              <td>NOT FOR REPRODUCTION OR DISTRIBUTION</td>
            </tr>
          </tfoot>
        )}
      </table>
    );
  };

  if (isHeaderFooterVisible) {
    return renderWithHeaderFooter();
  }
  return (
    <div ref={ref} className={cx('body')} data-testid={DATA_TEST_ID}>
      {renderPrintIcon()}
      {children}
    </div>
  );
};

export default forwardRef(PrintView);
